import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useDropzone } from 'react-dropzone';
import {
  ConfirmInvestmentTransactionsImportResponse,
  InvestorListDTO,
  PreviewInvestmentTransactionsImportResponse,
  TransactionImportAmountStrategy,
  TransactionImportConfidenceLevel,
  TransactionImportMappingDTO,
  TransactionImportRowOverrideDTO,
  TransactionType,
  PaymentMethod,
  InvestmentType,
} from '@inversor-hub/types';
import {
  useAnalyzeInvestmentTransactionsImport,
  useConfirmInvestmentTransactionsImport,
  usePreviewInvestmentTransactionsImport,
} from '@/lib/api/investment-transactions';
import { paymentMethodLabels, transactionTypeLabels } from './constants';
import { PreviewStep } from './ImportWizard/PreviewStep';
import { ImportProcessingView } from './ImportWizard/ImportProcessingView';

interface TransactionImportWizardProps {
  open: boolean;
  investmentId?: string;
  investmentType?: InvestmentType;
  investments?: InvestorListDTO[];
  onClose: () => void;
  onImported: () => void;
}

const steps = ['Archivo', 'Mapeo', 'Preview', 'Resultado'];

const fieldLabels: Record<keyof TransactionImportMappingDTO['columns'], string> = {
  date: 'Fecha',
  amount: 'Importe',
  type: 'Tipo',
  concept: 'Concepto',
  counterparty: 'Ordenante / contraparte',
  paymentMethod: 'Método de pago',
};

const amountStrategyLabels: Record<TransactionImportAmountStrategy, string> = {
  SIGNED_AMOUNT: 'Importe con signo',
  AMOUNT_WITH_TYPE: 'Importe + columna tipo',
};

const typeOptions = Object.keys(transactionTypeLabels) as TransactionType[];
const paymentMethodOptions = Object.keys(paymentMethodLabels) as PaymentMethod[];

export const TransactionImportWizard = ({
  open,
  investmentId,
  investmentType,
  investments: providedInvestments,
  onClose,
  onImported,
}: TransactionImportWizardProps) => {
  const isGlobalImport = !investmentId;
  const investments = useMemo(() => providedInvestments ?? [], [providedInvestments]);
  const roomRentInvestmentIds = useMemo(
    () =>
      investmentId
        ? investmentType === 'ROOM_RENT'
          ? [investmentId]
          : []
        : investments
            .filter((investment) => investment.type === 'ROOM_RENT')
            .map((investment) => investment.id),
    [investmentId, investmentType, investments]
  );
  const showRoomColumn = roomRentInvestmentIds.length > 0;
  const analyzeMutation = useAnalyzeInvestmentTransactionsImport(investmentId);
  const previewMutation = usePreviewInvestmentTransactionsImport(investmentId);
  const confirmMutation = useConfirmInvestmentTransactionsImport(investmentId);
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [mapping, setMapping] = useState<TransactionImportMappingDTO | null>(null);
  const [includeDuplicates, setIncludeDuplicates] = useState(false);
  const [rowOverrides, setRowOverrides] = useState<TransactionImportRowOverrideDTO[]>([]);
  const [rowOverridesDirty, setRowOverridesDirty] = useState(false);
  const [preview, setPreview] = useState<
    PreviewInvestmentTransactionsImportResponse['data'] | null
  >(null);
  const [result, setResult] = useState<ConfirmInvestmentTransactionsImportResponse['data'] | null>(
    null
  );

  const analysis = analyzeMutation.data;
  const columns = useMemo(
    () => analysis?.columnMappings.map((column) => column.sourceColumn) ?? [],
    [analysis]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024,
    onDrop: (acceptedFiles) => {
      const selectedFile = acceptedFiles[0];
      if (!selectedFile) return;
      setFile(selectedFile);
      setMapping(null);
      setRowOverrides([]);
      setRowOverridesDirty(false);
      setPreview(null);
      setResult(null);
      setActiveStep(0);
    },
  });

  const resetAndClose = () => {
    setActiveStep(0);
    setFile(null);
    setMapping(null);
    setRowOverrides([]);
    setRowOverridesDirty(false);
    setPreview(null);
    setResult(null);
    setIncludeDuplicates(false);
    analyzeMutation.reset();
    previewMutation.reset();
    confirmMutation.reset();
    onClose();
  };

  const goBack = () => {
    if (activeStep <= 0 || activeStep === 3 || isBusy) return;
    if (activeStep === 2) {
      setPreview(null);
    }
    setActiveStep((current) => Math.max(0, current - 1));
  };

  const analyzeFile = async (sheetName?: string) => {
    if (!file) return;
    const nextAnalysis = await analyzeMutation.mutateAsync({ file, sheetName });
    setMapping(nextAnalysis.mapping);
    setRowOverrides([]);
    setRowOverridesDirty(false);
    setPreview(null);
    setActiveStep(1);
  };

  const updateMapping = (
    updater: (current: TransactionImportMappingDTO) => TransactionImportMappingDTO
  ) => {
    setMapping((current) => {
      if (!current) return current;
      setRowOverrides([]);
      setRowOverridesDirty(false);
      setPreview(null);
      return updater(current);
    });
  };

  const runPreview = async (withIncludeDuplicates: boolean) => {
    if (!file || !mapping) return;
    const nextPreview = await previewMutation.mutateAsync({
      file,
      mapping,
      includeDuplicates: withIncludeDuplicates,
      rowOverrides,
    });
    setPreview(nextPreview);
    setRowOverrides(nextPreview.rowOverrides);
    setRowOverridesDirty(false);
    setActiveStep(2);
  };

  const previewImport = () => runPreview(includeDuplicates);

  const confirmImport = async () => {
    if (!file || !mapping) return;
    const nextResult = await confirmMutation.mutateAsync({
      file,
      mapping,
      includeDuplicates,
      rowOverrides,
    });
    setResult(nextResult);
    setActiveStep(3);
    onImported();
  };

  const canPreview = Boolean(mapping?.columns.date && mapping?.columns.amount);
  const isBusy =
    analyzeMutation.isPending || previewMutation.isPending || confirmMutation.isPending;
  const isGeneratingPreview = previewMutation.isPending && activeStep === 1;
  const isAnalyzing = analyzeMutation.isPending && activeStep === 0;
  const selectedSheetRowCount = mapping
    ? analysis?.sheets.find((sheet) => sheet.name === mapping.sheetName)?.rowCount
    : undefined;
  const stepperActiveStep = isAnalyzing ? 1 : isGeneratingPreview ? 2 : activeStep;
  const isPreviewStep = activeStep === 2 && Boolean(preview);

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth={isPreviewStep ? 'xl' : 'lg'}
      fullWidth
      PaperProps={{
        sx: isPreviewStep ? { height: '92vh', maxHeight: '92vh', m: { xs: 1, sm: 2 } } : undefined,
      }}
    >
      <DialogTitle>Importar transacciones</DialogTitle>
      <DialogContent
        dividers
        sx={
          isPreviewStep
            ? { display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 2 } }
            : undefined
        }
      >
        <Stack
          spacing={isPreviewStep ? 1.5 : 3}
          sx={isPreviewStep ? { flex: 1, minHeight: 0 } : undefined}
        >
          {!isPreviewStep && (
            <Stepper activeStep={stepperActiveStep} alternativeLabel>
              {steps.map((step) => (
                <Step key={step}>
                  <StepLabel>{step}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {isAnalyzing && <ImportProcessingView variant="analyze" />}

          {activeStep === 0 && !isAnalyzing && (
            <Stack spacing={2}>
              {isGlobalImport ? (
                <Alert severity="info">
                  <Typography variant="body2" gutterBottom>
                    Vas a importar movimientos de varias inversiones. Asignaremos cada uno y podrás
                    revisarlo antes de confirmar.
                  </Typography>
                  <Typography variant="body2">
                    ¿Son todos de la misma inversión? Te resultará más fácil importarlos desde esa
                    inversión, así no tendrás que asignarlos uno a uno.
                  </Typography>
                </Alert>
              ) : null}
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                  cursor: 'pointer',
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <input {...getInputProps()} />
                <UploadFileIcon sx={{ color: 'primary.main', fontSize: 42, mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  {file ? file.name : 'Arrastra un Excel o CSV'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Formatos aceptados: .xlsx, .xls y .csv. Máximo 20MB.
                </Typography>
              </Box>

              {analyzeMutation.isError && (
                <Alert severity="error">No hemos podido analizar el archivo.</Alert>
              )}
            </Stack>
          )}

          {isGeneratingPreview && (
            <ImportProcessingView variant="preview" rowCount={selectedSheetRowCount} />
          )}

          {activeStep === 1 && !isGeneratingPreview && analysis && mapping && (
            <Stack spacing={3}>
              {analysis.multipleSheetsDetected && (
                <FormControl fullWidth>
                  <InputLabel>Hoja</InputLabel>
                  <Select
                    label="Hoja"
                    value={analysis.selectedSheetName}
                    onChange={(event) => analyzeFile(event.target.value)}
                    disabled={isBusy}
                  >
                    {analysis.sheets.map((sheet) => (
                      <MenuItem key={sheet.name} value={sheet.name}>
                        {sheet.name} · {sheet.rowCount} filas
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Estrategia de importe</InputLabel>
                    <Select
                      label="Estrategia de importe"
                      value={mapping.amountStrategy}
                      onChange={(event) =>
                        updateMapping((current) => ({
                          ...current,
                          amountStrategy: event.target.value as TransactionImportAmountStrategy,
                        }))
                      }
                    >
                      {Object.entries(amountStrategyLabels).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Fila de encabezado detectada
                  </Typography>
                  <Typography variant="h6">{mapping.headerRowIndex + 1}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Confianza
                  </Typography>
                  <ConfidenceChip confidence={analysis.headerConfidence} />
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Mapeo de columnas
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Campo InversorHub</TableCell>
                        <TableCell>Columna del archivo</TableCell>
                        <TableCell>Confianza</TableCell>
                        <TableCell>Ejemplos</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(
                        Object.keys(fieldLabels) as Array<
                          keyof TransactionImportMappingDTO['columns']
                        >
                      )
                        .filter(
                          (field) =>
                            (mapping.amountStrategy === 'AMOUNT_WITH_TYPE' || field !== 'type') &&
                            (showRoomColumn || field !== 'counterparty')
                        )
                        .map((field) => {
                          const selectedColumn = mapping.columns[field] ?? '';
                          const detectedColumn = analysis.columnMappings.find(
                            (column) => column.sourceColumn === selectedColumn
                          );
                          return (
                            <TableRow key={field}>
                              <TableCell>{fieldLabels[field]}</TableCell>
                              <TableCell>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={selectedColumn}
                                    displayEmpty
                                    onChange={(event) =>
                                      updateMapping((current) => ({
                                        ...current,
                                        columns: {
                                          ...current.columns,
                                          [field]: event.target.value || undefined,
                                        },
                                      }))
                                    }
                                  >
                                    <MenuItem value="">Sin mapear</MenuItem>
                                    {columns.map((column) => (
                                      <MenuItem key={column} value={column}>
                                        {column}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell>
                                {detectedColumn ? (
                                  <ConfidenceChip confidence={detectedColumn.confidence} />
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 360 }}>
                                {detectedColumn && detectedColumn.sampleValues.length > 0 ? (
                                  <Stack spacing={0.25}>
                                    {detectedColumn.sampleValues.slice(0, 3).map((sample, idx) => (
                                      <Tooltip
                                        key={`${sample}-${idx}`}
                                        title={sample}
                                        placement="top-start"
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontFamily: 'monospace',
                                          }}
                                        >
                                          {sample}
                                        </Typography>
                                      </Tooltip>
                                    ))}
                                  </Stack>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Divider />

              <Grid container spacing={3}>
                {mapping.amountStrategy === 'AMOUNT_WITH_TYPE' && (
                  <Grid item xs={12} md={6}>
                    <MappingSelectList
                      title="Tipos"
                      emptyText="No se han detectado tipos en el archivo."
                      rows={mapping.typeMappings}
                      getSource={(row) => row.sourceValue}
                      getValue={(row) => row.type}
                      renderValue={(value) => transactionTypeLabels[value as TransactionType]}
                      options={typeOptions}
                      optionLabels={transactionTypeLabels}
                      onChange={(sourceValue, type) =>
                        updateMapping((current) => ({
                          ...current,
                          typeMappings: current.typeMappings.map((row) =>
                            row.sourceValue === sourceValue
                              ? { ...row, type: type as TransactionType }
                              : row
                          ),
                        }))
                      }
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={mapping.amountStrategy === 'AMOUNT_WITH_TYPE' ? 6 : 12}>
                  <MappingSelectList
                    title="Métodos de pago"
                    emptyText="No se han detectado métodos de pago."
                    rows={mapping.paymentMethodMappings}
                    getSource={(row) => row.sourceValue}
                    getValue={(row) => row.paymentMethod}
                    renderValue={(value) => paymentMethodLabels[value as PaymentMethod]}
                    options={paymentMethodOptions}
                    optionLabels={paymentMethodLabels}
                    onChange={(sourceValue, paymentMethod) =>
                      updateMapping((current) => ({
                        ...current,
                        paymentMethodMappings: current.paymentMethodMappings.map((row) =>
                          row.sourceValue === sourceValue
                            ? { ...row, paymentMethod: paymentMethod as PaymentMethod }
                            : row
                        ),
                      }))
                    }
                  />
                </Grid>
              </Grid>

              {!canPreview && (
                <Alert severity="warning">
                  Necesitamos al menos fecha e importe para previsualizar.
                </Alert>
              )}
            </Stack>
          )}

          {activeStep === 2 && preview && (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <PreviewStep
                preview={preview}
                investments={investments}
                showInvestmentColumn={isGlobalImport}
                showRoomColumn={showRoomColumn}
                roomRentInvestmentIds={roomRentInvestmentIds}
                rowOverrides={rowOverrides}
                includeDuplicates={includeDuplicates}
                rowOverridesDirty={rowOverridesDirty}
                isRefreshing={previewMutation.isPending}
                isConfirming={confirmMutation.isPending}
                onIncludeDuplicatesChange={(value) => {
                  setIncludeDuplicates(value);
                  runPreview(value);
                }}
                onRowOverrideChange={(rowNumber, patch) => {
                  setRowOverrides((current) => updateRowOverrides(current, rowNumber, patch));
                  setRowOverridesDirty(true);
                }}
                onRefreshPreview={previewImport}
                onConfirm={confirmImport}
                onBack={goBack}
                onCancel={resetAndClose}
                isBusy={isBusy}
              />
            </Box>
          )}

          {activeStep === 3 && result && (
            <Stack spacing={2} alignItems="flex-start">
              <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 42 }} />
              <Typography variant="h6">Importación completada</Typography>
              <Typography variant="body2" color="text.secondary">
                Se han importado {result.importedCount} transacciones. Omitidas:{' '}
                {result.skippedCount}. Duplicados detectados: {result.duplicateCount}.
              </Typography>
              {result.skippedRows.length > 0 && (
                <Alert severity="warning">
                  Algunas filas quedaron sin importar. Puedes revisar el archivo y repetir la
                  importación solo con esas filas.
                </Alert>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      {!isPreviewStep && (
        <DialogActions>
          <Button onClick={resetAndClose} disabled={isBusy}>
            {activeStep === 3 ? 'Cerrar' : 'Cancelar'}
          </Button>
          {activeStep > 0 && activeStep < 3 && (
            <Button onClick={goBack} disabled={isBusy}>
              Atrás
            </Button>
          )}
          {activeStep === 0 && !isAnalyzing && (
            <Button
              variant="contained"
              onClick={() => analyzeFile()}
              disabled={!file || analyzeMutation.isPending}
            >
              Analizar archivo
            </Button>
          )}
          {activeStep === 1 && !isGeneratingPreview && (
            <Button
              variant="contained"
              onClick={previewImport}
              disabled={!canPreview || previewMutation.isPending}
            >
              Previsualizar
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

const ConfidenceChip = ({ confidence }: { confidence: TransactionImportConfidenceLevel }) => {
  const label = confidence === 'HIGH' ? 'Alta' : confidence === 'MEDIUM' ? 'Media' : 'Baja';
  const color = confidence === 'HIGH' ? 'success' : confidence === 'MEDIUM' ? 'warning' : 'error';
  return <Chip label={label} color={color} size="small" variant="outlined" />;
};

function MappingSelectList<
  T extends { sourceValue: string; confidence: TransactionImportConfidenceLevel },
>(props: {
  title: string;
  emptyText: string;
  helperText?: string;
  rows: T[];
  getSource: (row: T) => string;
  getValue: (row: T) => string;
  renderValue: (value: string) => string;
  options: string[];
  optionLabels: Record<string, string>;
  onChange: (sourceValue: string, value: string) => void;
}) {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" fontWeight={600}>
        {props.title}
      </Typography>
      {props.helperText && (
        <Typography variant="caption" color="text.secondary">
          {props.helperText}
        </Typography>
      )}
      {props.rows.length === 0 ? (
        <Alert severity="info">{props.emptyText}</Alert>
      ) : (
        props.rows.map((row) => (
          <Grid container spacing={1} alignItems="center" key={props.getSource(row)}>
            <Grid item xs={5}>
              <Typography variant="body2" noWrap>
                {props.getSource(row)}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <FormControl size="small" fullWidth>
                <Select
                  value={props.getValue(row)}
                  renderValue={(value) => props.renderValue(value)}
                  onChange={(event) => props.onChange(props.getSource(row), event.target.value)}
                >
                  {props.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {props.optionLabels[option]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={2}>
              <ConfidenceChip confidence={row.confidence} />
            </Grid>
          </Grid>
        ))
      )}
    </Stack>
  );
}

function updateRowOverrides(
  current: TransactionImportRowOverrideDTO[],
  rowNumber: number,
  patch: Omit<TransactionImportRowOverrideDTO, 'rowNumber'>
): TransactionImportRowOverrideDTO[] {
  const existing = current.find((override) => override.rowNumber === rowNumber);
  const next = { ...existing, rowNumber, ...patch };
  const hasConcept = next.concept !== undefined;
  const hasInvestment = Boolean(next.investmentId);
  const hasCategory = Boolean(next.category);
  const hasLines = Boolean(next.lines && next.lines.length > 0);
  const hasSkip = Boolean(next.skip);
  const hasRoom = Boolean(next.roomId);
  const hasRentAllocations = next.rentAllocations !== undefined;
  const rest = current.filter((override) => override.rowNumber !== rowNumber);
  return hasInvestment ||
    hasConcept ||
    hasCategory ||
    hasLines ||
    hasSkip ||
    hasRoom ||
    hasRentAllocations
    ? [...rest, next]
    : rest;
}
